import { createFileRoute } from '@tanstack/react-router';
import { BellRing, Info, ShieldAlert, TestTubeDiagonal } from 'lucide-react';
import { useCallback, useState } from 'react';

import { AdminModeBadge } from '@apps/web/components/admin/admin_mode_badge';
import { NotificationBell } from '@apps/web/components/notification_bell';
import { NotificationTabTitle } from '@apps/web/components/notification_tab_title';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationEvents } from '@apps/web/hooks/use_organization_events';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    type BrowserNotificationPermissionState,
    ensureBrowserNotificationPermission,
    readBrowserNotificationPermission,
} from '@apps/web/lib/browser_notifications';
import {
    useNotificationPreferences,
    useUser,
} from '@apps/web/swr_data_fetching/current_user_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const Route = createFileRoute('/_app/admin/notifications-lab')({
    head: () => ({
        meta: [
            {
                title: 'Notifications Lab | Phoebe Admin',
            },
        ],
    }),
    component: AdminNotificationsLabPage,
});

type NotificationTestType =
    | 'notification_info'
    | 'notification_success'
    | 'notification_warning'
    | 'notification_error';

const DEFAULT_TITLE = 'Pending Shift Confirmation to Test Caregiver';
const DEFAULT_MESSAGE = 'Needs review: “Can you take the 7 AM shift tomorrow?”';
const TEST_NOTIFICATION_STATUS = 'shift_confirmation_staged_messages_reminder';

function getStatusBadgeClassName(enabled: boolean): string {
    return enabled
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300'
        : 'border-border bg-muted/50 text-muted-foreground';
}

function AdminNotificationsLabPage() {
    const organizationId = useOrganizationId();
    const adminMode = useAdminMode();
    const { data: user } = useUser({ reason: 'admin-notifications-lab' });
    const { data: organization } = useOrganization(
        organizationId,
        {
            reason: 'admin-notifications-lab',
        },
        adminMode,
    );
    const {
        data: notificationPreferences,
        mutate: mutateNotificationPreferences,
    } = useNotificationPreferences(
        organizationId,
        'admin-notifications-lab',
        adminMode,
    );
    useOrganizationEvents(organizationId, user?.id ?? null, {
        browserNotificationsEnabled: true,
        telemetrySurface: 'web_app',
        mode: adminMode,
    });

    const [notificationTitle, setNotificationTitle] = useState(DEFAULT_TITLE);
    const [notificationMessage, setNotificationMessage] =
        useState(DEFAULT_MESSAGE);
    const [notificationType, setNotificationType] =
        useState<NotificationTestType>('notification_info');
    const [permissionState, setPermissionState] =
        useState<BrowserNotificationPermissionState>(() =>
            readBrowserNotificationPermission(),
        );
    const [pendingAction, setPendingAction] = useState<
        'prepare' | 'direct' | 'inapp' | 'browser' | null
    >(null);

    const isBrowserNotificationsSupported = permissionState !== 'unsupported';
    const canDispatchServerBackedNotificationTests = organizationId !== null;
    const reminderPreference =
        notificationPreferences?.shift_confirmation_staged_messages_reminder;

    const refreshPermissionState = useCallback(() => {
        setPermissionState(readBrowserNotificationPermission());
    }, []);

    const requestPermission = useCallback(async (): Promise<boolean> => {
        const permission = await ensureBrowserNotificationPermission();
        setPermissionState(permission);

        if (permission === 'unsupported') {
            toast.error(
                'Browser notifications are not supported in this browser.',
                { persistNotification: false },
            );
            return false;
        }

        if (permission !== 'granted') {
            toast.error(
                permission === 'denied'
                    ? 'Browser notifications are blocked for Phoebe in this browser.'
                    : 'Browser notification permission was not granted.',
                { persistNotification: false },
            );
            return false;
        }

        return true;
    }, []);

    const patchNotificationPreferences = useCallback(
        async (body: {
            readonly browser_enabled?: boolean;
            readonly shift_confirmation_staged_messages_reminder?: {
                readonly email: boolean;
                readonly inapp: boolean;
                readonly sms: boolean;
            };
        }) => {
            if (organizationId === null) {
                throw new Error('Select an organization first.');
            }

            await getApiClient().patch({
                url: '/users/me/notification-preferences',
                queryParams: { organization_id: organizationId },
                mode: adminMode,
                body,
            });
            await mutateNotificationPreferences();
        },
        [adminMode, mutateNotificationPreferences, organizationId],
    );

    const enableTestPreferences = useCallback(
        async (options: { readonly browserEnabled: boolean }) => {
            await patchNotificationPreferences({
                ...(options.browserEnabled ? { browser_enabled: true } : {}),
                shift_confirmation_staged_messages_reminder: {
                    email: true,
                    inapp: true,
                    sms: false,
                },
            });
        },
        [patchNotificationPreferences],
    );

    const dispatchServerBackedNotificationTest = useCallback(async () => {
        if (organizationId === null) {
            toast.error('Select an organization first.', {
                persistNotification: false,
            });
            return;
        }

        await getApiClient().post({
            url: '/admin/extension-lab/dispatch-notification',
            queryParams: { mode: adminMode },
            mode: adminMode,
            body: {
                organization_id: organizationId,
                type: notificationType,
                title: notificationTitle.trim() || DEFAULT_TITLE,
                message: notificationMessage.trim() || DEFAULT_MESSAGE,
            },
            reason: 'admin-notifications-lab-dispatch',
        });
    }, [
        adminMode,
        notificationMessage,
        notificationTitle,
        notificationType,
        organizationId,
    ]);

    const handlePrepareBrowserNotifications = useCallback(async () => {
        setPendingAction('prepare');
        try {
            const permissionGranted = await requestPermission();
            if (!permissionGranted) {
                return;
            }

            await enableTestPreferences({ browserEnabled: true });
            toast.success(
                'Browser notification testing is enabled for your current user.',
                { persistNotification: false },
            );
        } catch {
            toast.error('Failed to prepare browser notification testing.', {
                persistNotification: false,
            });
        } finally {
            refreshPermissionState();
            setPendingAction(null);
        }
    }, [enableTestPreferences, refreshPermissionState, requestPermission]);

    const handleDirectNotification = useCallback(async () => {
        setPendingAction('direct');
        try {
            const permissionGranted = await requestPermission();
            if (!permissionGranted) {
                return;
            }

            new Notification(`Phoebe | ${notificationTitle || DEFAULT_TITLE}`, {
                body: notificationMessage || DEFAULT_MESSAGE,
                tag: 'phoebe-admin-notifications-lab-direct',
            });
            toast.success('Direct browser notification sent.', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to send a direct browser notification.', {
                persistNotification: false,
            });
        } finally {
            refreshPermissionState();
            setPendingAction(null);
        }
    }, [
        notificationMessage,
        notificationTitle,
        refreshPermissionState,
        requestPermission,
    ]);

    const handleDispatchInAppNotificationTest = useCallback(async () => {
        setPendingAction('inapp');
        try {
            if (!canDispatchServerBackedNotificationTests) {
                toast.error(
                    'Server-backed notification tests need an organization with in-app notifications enabled.',
                    { persistNotification: false },
                );
                return;
            }

            await enableTestPreferences({ browserEnabled: false });
            await dispatchServerBackedNotificationTest();
            toast.success('Server-backed in-app notification sent.', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to dispatch the server-backed notification.', {
                persistNotification: false,
            });
        } finally {
            setPendingAction(null);
        }
    }, [
        canDispatchServerBackedNotificationTests,
        dispatchServerBackedNotificationTest,
        enableTestPreferences,
    ]);

    const handleDispatchBrowserNotificationTest = useCallback(async () => {
        setPendingAction('browser');
        try {
            if (!canDispatchServerBackedNotificationTests) {
                toast.error(
                    'Server-backed browser notification tests need an organization with in-app notifications enabled.',
                    { persistNotification: false },
                );
                return;
            }

            const permissionGranted = await requestPermission();
            if (!permissionGranted) {
                return;
            }

            await enableTestPreferences({ browserEnabled: true });

            const helperWindow = window.open('', '_blank');
            if (helperWindow === null) {
                toast.error(
                    'Popup blocked. Allow popups for Phoebe or use the direct notification button instead.',
                    { persistNotification: false },
                );
                return;
            }

            helperWindow.document.title = 'Phoebe Notification Test Helper';
            helperWindow.document.body.innerHTML = `
                <main style="font-family: ui-sans-serif, system-ui, sans-serif; padding: 24px; line-height: 1.5;">
                    <h1 style="font-size: 18px; margin: 0 0 8px;">Phoebe notification helper</h1>
                    <p style="margin: 0;">Keep this tab focused for a moment while Phoebe sends a real test notification from the current organization.</p>
                </main>
            `;
            helperWindow.focus();

            window.setTimeout(() => {
                void dispatchServerBackedNotificationTest().catch(() => {
                    toast.error(
                        'Failed to dispatch the server-backed browser notification.',
                        { persistNotification: false },
                    );
                });
            }, 400);
            toast.success(
                'Opened a helper tab and queued a server-backed browser notification test.',
                { persistNotification: false },
            );
        } catch {
            toast.error(
                'Failed to queue the server-backed browser notification test.',
                { persistNotification: false },
            );
        } finally {
            refreshPermissionState();
            setPendingAction(null);
        }
    }, [
        canDispatchServerBackedNotificationTests,
        dispatchServerBackedNotificationTest,
        enableTestPreferences,
        refreshPermissionState,
        requestPermission,
    ]);

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
            <NotificationTabTitle />
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline">Unlisted</Badge>
                        <Badge variant="outline">Admin only</Badge>
                    </div>
                    <h1 className="text-2xl font-semibold">
                        Notifications Test Lab
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Manual test surface for current-user notification
                        permissions, preferences, in-app delivery, and browser
                        notification delivery. This page is intentionally not
                        linked from the admin sidebar.
                    </p>
                </div>
                <div
                    aria-label="Live in-app notification surface"
                    className="bg-background flex items-center gap-3 rounded-lg border px-3 py-2 shadow-sm"
                    role="group"
                >
                    <div className="text-right">
                        <p className="text-foreground text-sm font-medium">
                            Live in-app surface
                        </p>
                        <p className="text-muted-foreground text-xs">
                            Bell state updates here when tests dispatch.
                        </p>
                    </div>
                    <NotificationBell telemetrySurface="web_app" />
                </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            Current Context
                        </CardTitle>
                        <CardDescription>
                            The lab uses your active admin session and current
                            organization context.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <div>
                            <span className="text-foreground font-medium">
                                User:
                            </span>{' '}
                            <span className="text-muted-foreground">
                                {user?.email ?? 'Loading…'}
                            </span>
                        </div>
                        <div>
                            <span className="text-foreground font-medium">
                                Organization:
                            </span>{' '}
                            <span className="text-muted-foreground">
                                {organization?.name ?? 'Loading…'}
                            </span>
                        </div>
                        <div>
                            <span className="text-foreground font-medium">
                                Mode:
                            </span>{' '}
                            <span className="text-muted-foreground capitalize">
                                {adminMode}
                            </span>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            Browser State
                        </CardTitle>
                        <CardDescription>
                            Direct browser notification support and permission
                            state for this browser profile.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                            <Badge
                                className={getStatusBadgeClassName(
                                    isBrowserNotificationsSupported,
                                )}
                                variant="outline"
                            >
                                {isBrowserNotificationsSupported
                                    ? 'Supported'
                                    : 'Unsupported'}
                            </Badge>
                            <Badge
                                className={getStatusBadgeClassName(
                                    permissionState === 'granted',
                                )}
                                variant="outline"
                            >
                                Permission: {permissionState}
                            </Badge>
                        </div>
                        <Button
                            onClick={() => {
                                void handlePrepareBrowserNotifications();
                            }}
                            type="button"
                            variant="outline"
                            disabled={pendingAction !== null}
                        >
                            <ShieldAlert className="mr-2 h-4 w-4" />
                            Prepare browser notification testing
                        </Button>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            Product Path
                        </CardTitle>
                        <CardDescription>
                            Publishes a real notification row for the current
                            organization and delivers the realtime event to your
                            current user session.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                            <Badge
                                className={getStatusBadgeClassName(
                                    canDispatchServerBackedNotificationTests,
                                )}
                                variant="outline"
                            >
                                Server-backed path{' '}
                                {canDispatchServerBackedNotificationTests
                                    ? 'ready'
                                    : 'unavailable'}
                            </Badge>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            Current test preference:{' '}
                            {reminderPreference?.inapp === true
                                ? 'enabled'
                                : 'disabled'}
                            {' · '}
                            Browser preference:{' '}
                            {notificationPreferences?.browser_enabled === true
                                ? 'enabled'
                                : 'disabled'}
                        </p>
                        <p className="text-muted-foreground text-sm">
                            These tests publish the same notification shape the
                            extension listens for, so the bell, toast,
                            tab-title, browser notification, and extension can
                            all react to the same event. Realtime delivery is
                            scoped to your current admin user so you can test
                            live mode without broadcasting fake notifications to
                            the whole organization.
                        </p>
                    </CardContent>
                </Card>
            </div>
            <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>How to use this page</AlertTitle>
                <AlertDescription>
                    <p>
                        Use the direct browser notification button for a plain
                        Notification API smoke test in any environment.
                    </p>
                    <p>
                        Use the server-backed buttons to publish a real Phoebe
                        notification for the current organization. That path
                        reaches the app bell and any connected extension session
                        for your current user.
                    </p>
                    <p>
                        The browser notification test still opens a helper tab
                        first so this page is no longer the active tab when the
                        event arrives.
                    </p>
                </AlertDescription>
            </Alert>
            {!canDispatchServerBackedNotificationTests ? (
                <Alert>
                    <AlertTitle>Server-backed test limits</AlertTitle>
                    <AlertDescription>
                        <p>
                            Select an organization with in-app notifications
                            enabled to exercise the full web and extension
                            notification path.
                        </p>
                        {organizationId ? (
                            <p>
                                You can still use the direct browser
                                notification button below in this environment.
                            </p>
                        ) : (
                            <p>Select an organization first.</p>
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}
            <Card>
                <CardHeader>
                    <CardTitle>Notification Payload</CardTitle>
                    <CardDescription>
                        Customize the sample title, message, and severity used
                        by both the direct browser notification and the
                        server-backed notification tests.
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="notification-test-title">Title</Label>
                        <Input
                            id="notification-test-title"
                            value={notificationTitle}
                            onChange={(event) => {
                                setNotificationTitle(event.target.value);
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="notification-test-type">Severity</Label>
                        <Select
                            value={notificationType}
                            onValueChange={(value: NotificationTestType) => {
                                setNotificationType(value);
                            }}
                        >
                            <SelectTrigger id="notification-test-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="notification_info">
                                    Info
                                </SelectItem>
                                <SelectItem value="notification_success">
                                    Success
                                </SelectItem>
                                <SelectItem value="notification_warning">
                                    Warning
                                </SelectItem>
                                <SelectItem value="notification_error">
                                    Error
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="notification-test-message">
                            Message
                        </Label>
                        <Textarea
                            id="notification-test-message"
                            rows={4}
                            value={notificationMessage}
                            onChange={(event) => {
                                setNotificationMessage(event.target.value);
                            }}
                        />
                    </div>
                </CardContent>
            </Card>
            <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Direct Browser Notification</CardTitle>
                        <CardDescription>
                            Sends a plain browser notification immediately using
                            the current title and message.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <Button
                            onClick={() => {
                                void handleDirectNotification();
                            }}
                            type="button"
                            disabled={pendingAction !== null}
                        >
                            <BellRing className="mr-2 h-4 w-4" />
                            Send direct browser notification
                        </Button>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Server-backed Notification Path</CardTitle>
                        <CardDescription>
                            Publishes a real notification for the current
                            organization so the web app and extension receive
                            the same payload.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <AdminModeBadge mode={adminMode} />
                            <Button
                                onClick={() => {
                                    void handleDispatchInAppNotificationTest();
                                }}
                                type="button"
                                variant="outline"
                                disabled={
                                    pendingAction !== null ||
                                    !canDispatchServerBackedNotificationTests
                                }
                            >
                                <TestTubeDiagonal className="mr-2 h-4 w-4" />
                                Dispatch in-app test
                            </Button>
                            <Button
                                onClick={() => {
                                    void handleDispatchBrowserNotificationTest();
                                }}
                                type="button"
                                disabled={
                                    pendingAction !== null ||
                                    !canDispatchServerBackedNotificationTests
                                }
                            >
                                <BellRing className="mr-2 h-4 w-4" />
                                Open helper tab and send browser notification
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            The server-backed test uses the current status key{' '}
                            <code>{TEST_NOTIFICATION_STATUS}</code> so it
                            matches Phoebe’s existing notification preference
                            model and extension notification filters, while
                            keeping delivery scoped to your current user.
                        </p>
                    </CardContent>
                </Card>
            </div>
            <p className="text-muted-foreground text-sm">
                For normal user-facing preference controls, use{' '}
                <a
                    className="text-foreground underline"
                    href="/settings?tab=user"
                >
                    Settings
                </a>
                .
            </p>
        </div>
    );
}
